import { Routes, Route, BrowserRouter, Navigate, Link } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { useAuth } from "@workos-inc/authkit-react";
import { api } from "../../convex/_generated/api";

import { DashboardLayout } from "@/components/dashboard-layout"
import { AccessGatePanel } from "@/components/access-gate-panel"
import { NotFoundPage } from "@/components/not-found-page"
import { Button } from "@/components/ui/button"
import { DataPreloader } from "@/frontend/DataPreloader";
import { ScrollContextProvider } from "@/contexts/scroll-context";
import { LocationProvider } from "@/contexts/location-context";
import { CacheStatusProvider } from "@/contexts/cache-status-context";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { DashboardPageSkeleton } from "@/components/page-skeletons";
import { useOfflineReportSync } from "@/hooks/use-offline-report-queue";
import { FirstVisitTutorialDialog } from "@/components/first-visit-tutorial-dialog";
import { PageMeta } from "@/components/page-meta";
import { TonightPrefetchRunner } from "@/components/tonight-prefetch-runner";

const Dashboard = lazy(() => import("@/frontend/features/dashboard"));
const Admin = lazy(() => import("@/frontend/features/admin"));
const LatestSubmissionsPage = lazy(() => import("@/frontend/features/latest-submissions"));
const MapPage = lazy(() => import("@/frontend/features/map-page"));
const SubmitAuroraPage = lazy(() => import("@/frontend/features/SubmitAuroraPage"));
const SettingsPage = lazy(() => import("@/frontend/features/settings"));
const AdvancedDashboard = lazy(() => import("@/frontend/features/advanced-dashboard"));
const LongTermForecastPage = lazy(() => import("@/frontend/features/long-term-forecast-page"));
const CookiePolicyPage = lazy(() => import("@/frontend/features/legal/cookie-policy-page"));
const PrivacyPolicyPage = lazy(() => import("@/frontend/features/legal/privacy-policy-page"));
const TermsPage = lazy(() => import("@/frontend/features/legal/terms-page"));
const PhotographyGuidePage = lazy(() => import("@/frontend/features/photography-guide-page"));
const StatusPage = lazy(() => import("@/frontend/features/status-page"));
const DebugPage = lazy(() => import("@/frontend/features/debug-page"));
const SuccessPage = lazy(() => import("@/frontend/features/success-page"));

function preloadRouteModules() {
    void import("@/frontend/features/dashboard");
    void import("@/frontend/features/admin");
    void import("@/frontend/features/latest-submissions");
    void import("@/frontend/features/map-page");
    void import("@/frontend/features/SubmitAuroraPage");
    void import("@/frontend/features/settings");
    void import("@/frontend/features/advanced-dashboard");
    void import("@/frontend/features/long-term-forecast-page");
    void import("@/frontend/features/legal/privacy-policy-page");
    void import("@/frontend/features/legal/cookie-policy-page");
    void import("@/frontend/features/legal/terms-page");
    void import("@/frontend/features/debug-page");
    void import("@/frontend/features/photography-guide-page");
    void import("@/frontend/features/status-page");
}

function OfflineReportSync() {
    useOfflineReportSync();
    return null;
}

function ForecastAccessGate() {
    const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
    const { signIn } = useAuth();
    const canAccessForecast = useQuery(api.users.canAccessAdvancedForecast);
    const subscription = useQuery(api.polar.getCurrentSubscription, {});

    const hasProAccess = canAccessForecast === true || (subscription !== undefined && subscription !== null);
    const isLoadingAccess =
        canAccessForecast === undefined || (canAccessForecast === false && subscription === undefined);

    if (authLoading || (isAuthenticated && isLoadingAccess)) {
        return (
            <AccessGatePanel title="Checking access" description="Verifying your forecast subscription.">
                <p className="text-sm text-muted-foreground">One moment…</p>
            </AccessGatePanel>
        );
    }

    if (!isAuthenticated) {
        return (
            <AccessGatePanel
                title="Pro feature"
                description="Sign in and upgrade to Pro to access the long-term forecast."
            >
                <Button type="button" onClick={() => signIn()} className="transition-transform duration-200 active:scale-[0.98]">
                    Sign in
                </Button>
            </AccessGatePanel>
        );
    }

    if (!hasProAccess) {
        return (
            <AccessGatePanel
                title="Upgrade required"
                description="Long-term forecast is available for Pro, Moderator, and Admin accounts."
            >
                <Button asChild variant="outline">
                    <Link to="/settings">View plans in Settings</Link>
                </Button>
            </AccessGatePanel>
        );
    }

    return <LongTermForecastPage />;
}

function AdminAccessGate({ children }: { children: React.ReactNode }) {
    const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
    const { signIn } = useAuth();
    const isAdmin = useQuery(api.users.isAdmin, isAuthenticated ? undefined : "skip");

    if (authLoading || (isAuthenticated && isAdmin === undefined)) {
        return (
            <AccessGatePanel title="Checking access" description="Verifying admin access.">
                <p className="text-sm text-muted-foreground">One moment…</p>
            </AccessGatePanel>
        );
    }

    if (!isAuthenticated) {
        return (
            <AccessGatePanel
                title="Admin only"
                description="Sign in with an admin account to view this page."
            >
                <Button type="button" onClick={() => signIn()} className="transition-transform duration-200 active:scale-[0.98]">
                    Sign in
                </Button>
            </AccessGatePanel>
        );
    }

    if (isAdmin !== true) {
        return (
            <AccessGatePanel
                title="Access denied"
                description="This page is only available to administrators."
            >
                <Button asChild variant="outline">
                    <Link to="/">Back to dashboard</Link>
                </Button>
            </AccessGatePanel>
        );
    }

    return <>{children}</>;
}

export default function App() {
    useEffect(() => {
        if (import.meta.env.DEV) {
            void import("@/lib/dexie-debug");
        }
    }, []);

    useEffect(() => {
        if ("requestIdleCallback" in window) {
            const id = window.requestIdleCallback(preloadRouteModules, { timeout: 2500 });
            return () => window.cancelIdleCallback(id);
        }
        const id = globalThis.setTimeout(preloadRouteModules, 750);
        return () => globalThis.clearTimeout(id);
    }, []);

    return (
        <BrowserRouter>
            <AppErrorBoundary context="generic">
                <PageMeta />
                <OfflineReportSync />
                <DataPreloader>
                    <CacheStatusProvider>
                    <ScrollContextProvider>
                        <LocationProvider>
                        <TonightPrefetchRunner>
                        <FirstVisitTutorialDialog />
                        <Suspense fallback={<DashboardPageSkeleton />}>
                            <Routes>
                                <Route element={<DashboardLayout />}>
                                    <Route index element={<Dashboard />} />
                                    <Route path="advanced-dashboard" element={<AdvancedDashboard />} />
                                    <Route path="forecast" element={<ForecastAccessGate />} />
                                    <Route path="wsa" element={<Navigate to="/forecast" replace />} />
                                    <Route path="latest-submissions" element={<LatestSubmissionsPage />} />
                                    <Route path="map" element={<MapPage />} />
                                    <Route path="photography" element={<PhotographyGuidePage />} />
                                    <Route
                                        path="status"
                                        element={
                                            <AdminAccessGate>
                                                <StatusPage />
                                            </AdminAccessGate>
                                        }
                                    />
                                    <Route path="my-reports" element={<Navigate to="/latest-submissions?filter=recently-reported" replace />} />
                                    <Route path="submit-report" element={<SubmitAuroraPage />} />
                                    <Route
                                        path="admin"
                                        element={
                                            <AdminAccessGate>
                                                <Admin />
                                            </AdminAccessGate>
                                        }
                                    />
                                    <Route path="debug" element={<DebugPage />} />
                                    <Route path="*" element={<NotFoundPage />} />
                                </Route>
                                <Route
                                    path="/settings"
                                    element={
                                        <AppErrorBoundary context="settings">
                                            <SettingsPage />
                                        </AppErrorBoundary>
                                    }
                                />
                                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                                <Route path="/cookies" element={<CookiePolicyPage />} />
                                <Route path="/terms" element={<TermsPage />} />
                                <Route path="/success" element={<SuccessPage />} />
                            </Routes>
                        </Suspense>
                        </TonightPrefetchRunner>
                        </LocationProvider>
                    </ScrollContextProvider>
                    </CacheStatusProvider>
                </DataPreloader>
            </AppErrorBoundary>
        </BrowserRouter>
    )

}